import { useCallback } from 'react'
import { usePageContext } from './context'
import type { PageAction, PageTabsConfig } from './types'

// Type guard to check if action is a component action
function isComponentAction(action: PageAction): action is Extract<PageAction, { type: 'component' }> {
	return action.type === 'component'
}

export function usePageHeaderActions() {
	const { actions, setPageActions, updatePageAction, clearPageActions } = usePageContext()

	const setActions = useCallback(
		(newActions: PageAction[]) => {
			// Validate required fields
			const invalidActions = newActions.filter(action => {
				if (!action.id) {
					return true
				}

				if (isComponentAction(action)) {
					// For component actions, validate component exists
					return !action.component
				}

				// For button actions, validate onClick exists
				if (typeof action.onClick !== 'function') {
					return true
				}

				// Allow empty label for icon-only buttons (they should have tooltip or icon)
				if (!action.label || action.label.trim() === '') {
					const hasIconOrTooltip = !!action.icon || !!(action as Extract<PageAction, { tooltip?: string }>).tooltip
					return !hasIconOrTooltip
				}

				return false
			})

			if (invalidActions.length > 0) {
				console.error('Invalid actions found:', invalidActions)
			}

			setPageActions(newActions)
		},
		[setPageActions],
	)

	const updateAction = useCallback(
		(actionId: string, updates: Partial<PageAction>) => {
			updatePageAction(actionId, updates)
		},
		[updatePageAction],
	)

	const setActionLoading = useCallback(
		(actionId: string, loading: boolean) => {
			updateAction(actionId, { loading })
		},
		[updateAction],
	)

	const setActionDisabled = useCallback(
		(actionId: string, disabled: boolean) => {
			updateAction(actionId, { disabled })
		},
		[updateAction],
	)

	const getAction = useCallback(
		(actionId: string): PageAction | undefined => {
			return actions.find(action => action.id === actionId)
		},
		[actions],
	)

	const hasAction = useCallback(
		(actionId: string): boolean => {
			return actions.some(action => action.id === actionId)
		},
		[actions],
	)

	return {
		actions,
		setActions,
		updateAction,
		clearPageActions,
		setActionLoading,
		setActionDisabled,
		getAction,
		hasAction,
	}
}

export function usePageHeaderTabs() {
	const { dynamicTabs, setPageTabs, clearPageTabs } = usePageContext()

	const setTabs = useCallback(
		(tabs: PageTabsConfig | null) => {
			setPageTabs?.(tabs)
		},
		[setPageTabs],
	)

	const clearTabs = useCallback(() => {
		clearPageTabs?.()
	}, [clearPageTabs])

	return {
		tabs: dynamicTabs,
		setTabs,
		clearTabs,
	}
}

export function usePageHeaderBackButton() {
	const { backButton, setBackButton, clearBackButton } = usePageContext()

	return {
		backButton,
		setBackButton,
		clearBackButton,
	}
}

/**
 * Opts a page into rendering its own controls in the header's action row, for pages that have no CTAs there.
 * Returns the slot to portal into, or null while the header has yet to mount it — so a caller can hold its
 * controls back for that one render instead of mounting them inline and remounting them into the portal.
 *
 * Off unless a page asks for it: pass `false` (the default) and this stays null, leaving the page to lay its
 * controls out itself. The header hides the slot whenever nothing is portalled in, so pages that never call
 * this are untouched.
 *
 * The slot lives inside the header's custom-view-creator branch, so a page that opts in *and* registers a
 * custom-views config would lose its controls while the creator is open. No caller does both today.
 */
export function usePageHeaderControls(enabled = false): HTMLElement | null {
	const { headerControlsSlot } = usePageContext()

	return enabled ? (headerControlsSlot ?? null) : null
}

export function usePageHeaderCustomViews() {
	const { hideCustomViews, setHideCustomViews } = usePageContext()

	return {
		hideCustomViews,
		setHideCustomViews,
	}
}
